import { useState, useEffect } from 'react'
import { useSearchParams, Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { UserPlus, CheckCircle2, XCircle, Clock, LogIn } from 'lucide-react'
import { api } from '@/api/client'
import type { InvitationPreview, User } from '@/api/types'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Spinner } from '@/components/ui/spinner'

export function AcceptInvitePage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [invitation, setInvitation] = useState<InvitationPreview | null>(null)
  const [loading, setLoading] = useState(true)
  const [tokenInput, setTokenInput] = useState(searchParams.get('token') || '')
  const [error, setError] = useState<string | null>(null)
  const [currentUser, setCurrentUser] = useState<User | null>(null)
  const [acting, setActing] = useState(false)
  const [done, setDone] = useState<string | null>(null)

  const token = searchParams.get('token')

  const fetchInvitation = async (t: string) => {
    if (!t) { setLoading(false); return }
    setLoading(true)
    setError(null)
    try {
      const inv = await api.getInvitationByToken(t)
      setInvitation(inv)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invitation not found')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    // Try to get the current logged-in user (best-effort, no redirect)
    api.getMe().then(setCurrentUser).catch(() => setCurrentUser(null))

    if (token) {
      setTokenInput(token)
      fetchInvitation(token)
    } else {
      setLoading(false)
    }
  }, [token])

  const handleLookup = () => {
    if (tokenInput.trim()) fetchInvitation(tokenInput.trim())
  }

  const handleAccept = async () => {
    if (!token) return
    setActing(true); setError(null)
    try {
      const res = await api.acceptInvitation(token)
      setDone(res.message)
      // Reload orgs so the new one appears in the switcher
      setTimeout(() => navigate('/'), 2000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to accept invitation')
    } finally { setActing(false) }
  }

  const handleDecline = async () => {
    if (!token) return
    setActing(true); setError(null)
    try {
      await api.declineInvitation(token)
      setDone('Invitation declined.')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to decline invitation')
    } finally { setActing(false) }
  }

  const emailMatch = currentUser && invitation && currentUser.email.toLowerCase() === invitation.email.toLowerCase()

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10">
            <UserPlus className="h-7 w-7 text-primary" />
          </div>
          <h1 className="text-xl font-bold tracking-tight">Accept Invitation</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Join an organisation on Ledger. Core
          </p>
        </div>

        {/* Token input (if no token in URL) */}
        {!token && !invitation && (
          <Card className="mb-4">
            <CardContent className="pt-6">
              <div className="space-y-3">
                <Label>Invitation Token</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Paste your invitation token"
                    value={tokenInput}
                    onChange={(e) => setTokenInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleLookup()}
                  />
                  <Button onClick={handleLookup} disabled={!tokenInput.trim() || loading}>
                    {loading ? <Spinner className="h-4 w-4" /> : 'Look Up'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {loading && (
          <div className="flex justify-center py-8">
            <Spinner className="h-6 w-6 text-primary" />
          </div>
        )}

        {/* Success state */}
        {done && (
          <Card>
            <CardContent className="py-8 text-center">
              <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-green-500" />
              <p className="text-sm font-medium">{done}</p>
              {done.includes('joined') && (
                <p className="mt-1 text-xs text-muted-foreground">Redirecting you now…</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Invitation details */}
        {invitation && !loading && !done && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Invitation Details</CardTitle>
                <Badge
                  variant={invitation.valid ? 'success' : 'destructive'}
                  className="gap-1"
                >
                  {invitation.valid ? (
                    <><CheckCircle2 className="h-3 w-3" /> Valid</>
                  ) : (
                    <><XCircle className="h-3 w-3" /> {invitation.status}</>
                  )}
                </Badge>
              </div>
              <CardDescription>
                You've been invited to join <strong>{invitation.organisation_name}</strong> as a{' '}
                <strong>{invitation.role}</strong>.
              </CardDescription>
            </CardHeader>

            {invitation.valid ? (
              <CardContent>
                <div className="space-y-4">
                  <div className="rounded-md bg-muted/50 p-3">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-muted-foreground">Email:</span>
                      <span className="font-medium">{invitation.email}</span>
                    </div>
                    <div className="mt-1 flex items-center gap-2 text-sm">
                      <span className="text-muted-foreground">Expires:</span>
                      <span className="flex items-center gap-1 font-medium">
                        <Clock className="h-3 w-3" />
                        {new Date(invitation.expires_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  {error && (
                    <p className="text-sm text-destructive">{error}</p>
                  )}

                  {/* Logged in as the right user — show Accept/Decline */}
                  {emailMatch ? (
                    <div className="flex gap-2">
                      <Button className="flex-1" onClick={handleAccept} disabled={acting}>
                        {acting ? <Spinner className="mr-2 h-4 w-4" /> : null}
                        Accept & Join
                      </Button>
                      <Button variant="outline" onClick={handleDecline} disabled={acting}>
                        Decline
                      </Button>
                    </div>
                  ) : currentUser ? (
                    /* Logged in as wrong account */
                    <div className="rounded-md border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20 p-3 text-sm text-amber-700 dark:text-amber-400">
                      You're signed in as <strong>{currentUser.email}</strong> but this invitation is for <strong>{invitation.email}</strong>. Please sign in with the correct account.
                    </div>
                  ) : (
                    /* Not logged in */
                    <>
                      <p className="text-sm text-muted-foreground">
                        Sign in with your <strong>{invitation.email}</strong> account to accept or decline this invitation.
                      </p>
                      <Link to={`/login?return_to=${encodeURIComponent(window.location.pathname + window.location.search)}`} className="block">
                        <Button className="w-full">
                          <LogIn className="mr-2 h-4 w-4" />
                          Sign In to Respond
                        </Button>
                      </Link>
                    </>
                  )}
                </div>
              </CardContent>
            ) : (
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  This invitation is <strong>{invitation.status}</strong> and can no longer be accepted.
                  Please ask for a new invitation.
                </p>
              </CardContent>
            )}
          </Card>
        )}

        {error && !invitation && !loading && (
          <Card>
            <CardContent className="py-8 text-center">
              <XCircle className="mx-auto mb-2 h-8 w-8 text-destructive/50" />
              <p className="text-sm text-destructive">{error}</p>
            </CardContent>
          </Card>
        )}

        <p className="mt-4 text-center text-xs text-muted-foreground">
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-primary hover:underline">
            Sign in
          </Link>
        </p>
      </motion.div>
    </div>
  )
}
